import { type CSSProperties } from "react";

/**
 * The "How it works" 3-step section shown below the hero on the
 * Accounting Overview empty state.
 *
 * Static content, no data dependencies. Step 2 references the Vela
 * intelligence feature with the "V" mark.
 */

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  vela: "#6B46C1",
  lineSoft: "#E7EAF0",
};

interface Step {
  number: number;
  title: string;
  description: string;
  isVela?: boolean;
}

const STEPS: Step[] = [
  {
    number: 1,
    title: "Connect your bank",
    description:
      "Secure read-only link pulls in new transactions daily. Or upload a CSV, or enter them yourself.",
  },
  {
    number: 2,
    title: "Vela categorizes",
    description:
      "Each transaction gets matched to the right account in your Chart of accounts. You approve or override.",
    isVela: true,
  },
  {
    number: 3,
    title: "Reconcile each month",
    description:
      "Match Novala's totals to your bank statement, sign off, and the period is locked.",
  },
];

export function HowItWorksSection() {
  return (
    <section style={sectionStyle}>
      <h2 style={labelStyle}>How it works</h2>
      {STEPS.map((step) => (
        <div key={step.number} style={stepStyle}>
          <div style={numStyle}>{step.number}</div>
          <div style={contentStyle}>
            <div style={titleStyle}>
              {step.isVela && <span style={vMarkStyle}>V</span>}
              {step.title}
            </div>
            <div style={descStyle}>{step.description}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

const sectionStyle: CSSProperties = {
  maxWidth: 720,
  margin: "32px auto 0",
  padding: "32px 20px 48px",
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const labelStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: PALETTE.ink3,
  fontWeight: 700,
  margin: "0 0 18px",
  textAlign: "center",
};

const stepStyle: CSSProperties = {
  display: "flex",
  gap: 20,
  padding: "18px 0",
};

const numStyle: CSSProperties = {
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: "50%",
  background: PALETTE.ink,
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
};

const contentStyle: CSSProperties = {
  flex: 1,
};

const titleStyle: CSSProperties = {
  fontSize: 14.5,
  fontWeight: 700,
  color: PALETTE.ink,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  lineHeight: 1.4,
};

const vMarkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: PALETTE.vela,
  color: "#ffffff",
  fontSize: 11,
  fontWeight: 700,
};

const descStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  marginTop: 4,
  lineHeight: 1.6,
};